'use strict';
'use es6';

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.UPLOAD_PATH = exports.BATCH_GET_PATH = exports.BASE_PATH = undefined;
exports.normalizeUpdates = normalizeUpdates;
exports.normalizeRetries = normalizeRetries;
exports.normalizeResponse = normalizeResponse;
exports.toRequestData = toRequestData;
exports.fetchAvatars = fetchAvatars;
exports.uploadAvatar = uploadAvatar;

var _immutable = require('immutable');

var _invariant = require('react-utils/invariant');

var _invariant2 = _interopRequireDefault(_invariant);

var _Constants = require('../Constants');

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

var BASE_PATH = exports.BASE_PATH = 'avatars/v1/';
var BATCH_GET_PATH = exports.BATCH_GET_PATH = 'avatars/batch';
var UPLOAD_PATH = exports.UPLOAD_PATH = 'overrides';

/**
 * Reduces the original lookups and the response into a Map of lookup => url.
 *
 * @param  {Set(LookupRecord)}
 * @param  {Object}
 * @return {Map<LookupRecord,?string>}
 */
function normalizeUpdates(lookups, response) {
  return lookups.reduce(function (updates, lookup) {
    if (!response[lookup.getIdentifierName()] || !response[lookup.getIdentifierName()][lookup.getIdentifier()] || !response[lookup.getIdentifierName()][lookup.getIdentifier()].uri) {
      return updates;
    }
    return updates.set(lookup, response[lookup.getIdentifierName()][lookup.getIdentifier()].uri);
  }, (0, _immutable.Map)());
}

/**
 * Reduces the original lookups and the response into a Set of lookups that are
 * queued and should be retried.
 *
 * @param  {Set(LookupRecord)}
 * @param  {Object}
 * @return {Set<LookupRecord>}
 */
function normalizeRetries(lookups, response) {
  return lookups.reduce(function (retries, lookup) {
    if (!response[lookup.getIdentifierName()] || !response[lookup.getIdentifierName()][lookup.getIdentifier()] || !response[lookup.getIdentifierName()][lookup.getIdentifier()].queued) {
      return retries;
    }
    return retries.add(lookup);
  }, (0, _immutable.Set)());
}

/**
 * @param  {Set(LookupRecord)}
 * @param  {Object}
 * @return {{retry: Set(LookupRecord), updates: Map<LookupRecord,?string>}}
 */
function normalizeResponse(lookups, response) {
  return {
    retry: normalizeRetries(lookups, response),
    updates: normalizeUpdates(lookups, response)
  };
}

/**
 * Takes a list of lookups in transforms them to a JSON structure that the api
 * understands.
 *
 * @param  {Set(LookupRecord)}
 * @param  {?number}
 * @return {Object}
 */
function toRequestData(lookups, dimension) {
  (0, _invariant2.default)(_immutable.Set.isSet(lookups), 'expected `lookups` to be a Set but got `%s`', lookups);
  (0, _invariant2.default)(typeof dimension === 'undefined' || typeof dimension === 'number', 'expected `dimension` to be undefined or a number but got `%s`', lookups);

  var requestDataWithoutDimensions = lookups.reduce(function (builder, lookup) {
    var withoutKnown = builder.update(lookup.getIdentifierName(), function (maybeSet) {
      return typeof maybeSet === 'undefined' ? _immutable.Set.of(lookup.getIdentifier()) : maybeSet.add(lookup.getIdentifier());
    });

    if (!lookup.isKnown()) {
      return withoutKnown;
    }

    return withoutKnown.update(lookup.getKnownName(), function (maybeSet) {
      return typeof maybeSet === 'undefined' ? _immutable.Set.of(lookup.getKnownObject()) : maybeSet.add(lookup.getKnownObject());
    });
  }, (0, _immutable.Map)());

  if (typeof dimension === 'number') {
    var doubleDimensionForRetina = dimension * _Constants.RETINA_MULTIPLIER;
    return requestDataWithoutDimensions.set('sizeParams', (0, _immutable.Map)({
      width: doubleDimensionForRetina,
      height: doubleDimensionForRetina
    })).toJS();
  }

  return requestDataWithoutDimensions.toJS();
}

/**
 * Uses `fetcher` to retrieve and then normalize responses from the API.
 *
 * @param  {(uri: string, data: Object) => Promise<Object>}
 * @param  {number || undefined} If undefined, will look for enrichment avatars only
 * @param  {Set(LookupRecord)}
 * @param  {?number}
 * @return {{retry: Set(LookupRecord), updates: Map<LookupRecord,?string>}}
 */
function fetchAvatars(fetcher, portalId, lookups, dimension) {
  (0, _invariant2.default)(typeof fetcher === 'function', 'expected `fetcher` to be a function but got `%s`', fetcher);
  (0, _invariant2.default)(typeof portalId === 'number' || typeof portalId === 'undefined', 'expected `portalId` to be a number or undefined but got `%s`', portalId);
  (0, _invariant2.default)(_immutable.Set.isSet(lookups), 'expected `lookups` to be a Set but got `%s`', lookups);
  (0, _invariant2.default)(typeof dimension === 'number' || typeof dimension === 'undefined', 'expected `dimension` to be a number or undefined but got `%s`', dimension);

  var portalIdQueryString = typeof portalId !== 'undefined' ? '?portalId=' + portalId : '';

  return fetcher('' + BASE_PATH + BATCH_GET_PATH + portalIdQueryString, toRequestData(lookups, dimension)).then(function () {
    for (var _len = arguments.length, partialArgs = Array(_len), _key = 0; _key < _len; _key++) {
      partialArgs[_key] = arguments[_key];
    }

    return normalizeResponse.apply(undefined, [lookups].concat(partialArgs));
  });
}

/**
 * Uses `uploader` to submit an avatar override
 * @param  {(uri: string, data: Object) => Promise<Object>} uploader
 * @param  {number} portalId
 * @param  {OneOf[...]} objectType
 * @param  {number} objectId
 * @param  {Blob([Uint8Array])} formData
 * @return {{uri: string, source: string, queued: bool, fromDefault: bool}}
 */
function uploadAvatar(uploader, portalId, objectType, objectId, formData) {
  (0, _invariant2.default)(typeof uploader === 'function', 'expected `uploader` to be a function but got `%s`', uploader);
  (0, _invariant2.default)(typeof portalId === 'number', 'expected `portalId` to be a number but got `%s`', portalId);
  var acceptableTypes = Object.keys(_Constants.OBJECT_TYPE_TO_ENDPOINT_MAPPINGS);
  (0, _invariant2.default)(acceptableTypes.indexOf(objectType) >= 0, 'expected `objectType` to be one of ' + acceptableTypes + ' but got `%s`', objectType);
  (0, _invariant2.default)(typeof objectId === 'number', 'expected `objectId` to be a number but got `%s`', objectId);
  (0, _invariant2.default)(formData instanceof FormData, 'expected `formData` to be an instance of FormData but got `%s`', formData);

  var endpoint = _Constants.OBJECT_TYPE_TO_ENDPOINT_MAPPINGS[objectType];
  return uploader('' + BASE_PATH + UPLOAD_PATH + '/' + endpoint + '/' + objectId + '?portalId=' + portalId, formData);
}